import {readStoredJson, writeStoredJson} from "./agent-local-storage";

export const AGENT_UI_STORAGE_KEY = 'agent_chat_ui';

export const AGENT_SHEET_MIN_HEIGHT = 240;
export const AGENT_SHEET_DEFAULT_HEIGHT = 384;      // 24rem — matches the legacy sheet
export const AGENT_SHEET_COLLAPSED_HEIGHT = 48;

const MIN_PAGE_ABOVE_SHEET = 160;
const MAX_VIEWPORT_FRACTION = 0.7;

export function clampSheetHeight(height, viewportHeight) {
    const viewport = Number.isFinite(viewportHeight) ? viewportHeight : 1000;

    const ceiling = Math.max(
        AGENT_SHEET_MIN_HEIGHT,
        Math.min(Math.round(viewport * MAX_VIEWPORT_FRACTION), viewport - MIN_PAGE_ABOVE_SHEET),
    );

    // The default is not automatically in range — on a short viewport 384px can
    // itself exceed the ceiling — so it goes through the same cap.
    if (typeof height !== 'number' || !Number.isFinite(height)) {
        return Math.min(AGENT_SHEET_DEFAULT_HEIGHT, ceiling);
    }

    return Math.min(Math.max(Math.round(height), AGENT_SHEET_MIN_HEIGHT), ceiling);
}

/**
 * Drives --agent-sheet-height. Closed MUST be exactly 0px so every page that
 * never opens the sheet renders byte-identically to before this feature.
 */
export function sheetHeightCssValue({isOpen, isCollapsed, sheetHeight}) {
    if (!isOpen) {
        return '0px';
    }

    if (isCollapsed) {
        return `${AGENT_SHEET_COLLAPSED_HEIGHT}px`;
    }

    return `${sheetHeight}px`;
}

export function loadAgentUiState() {
    const stored = readStoredJson(AGENT_UI_STORAGE_KEY, {}) ?? {};
    const viewportHeight = typeof window === 'undefined' ? 1000 : window.innerHeight;
    // Clamped, because the default can exceed the ceiling on a short viewport.
    const fallbackHeight = clampSheetHeight(AGENT_SHEET_DEFAULT_HEIGHT, viewportHeight);

    const storedHeight = typeof stored.sheetHeight === 'number'
        ? clampSheetHeight(stored.sheetHeight, viewportHeight)
        : fallbackHeight;

    return {
        isOpen: stored.isOpen === true,
        isCollapsed: stored.isCollapsed === true,
        // A height that no longer fits (smaller monitor, or a tampered value)
        // falls back rather than wedging the sheet off-screen.
        sheetHeight: storedHeight === stored.sheetHeight ? storedHeight : fallbackHeight,
        activeThreadId: stored.activeThreadId ?? null,
    };
}

export function saveAgentUiState(state) {
    writeStoredJson(AGENT_UI_STORAGE_KEY, {
        isOpen: state.isOpen === true,
        isCollapsed: state.isCollapsed === true,
        sheetHeight: state.sheetHeight,
        activeThreadId: state.activeThreadId ?? null,
    });
}
